import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Paperclip, FileText, Image as ImageIcon, X, Loader2, Download, Camera, RotateCcw } from "lucide-react";
import { api } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

const MB = 1024 * 1024;
const fmtSize = (n) => (n >= MB ? `${(n / MB).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const errText = (err, fallback) => (typeof err.response?.data?.detail === "string" ? err.response.data.detail : fallback);

function useUploader() {
  const [pct, setPct] = useState(null);
  const upload = async (url, form) => {
    setPct(0);
    try {
      return await api.post(url, form, { onUploadProgress: (e) => e.total && setPct(Math.round((e.loaded / e.total) * 100)) });
    } finally { setPct(null); }
  };
  return { pct, upload };
}

export function RuleDocuments({ propertyId, ruleCode, docs, onChanged, onPreview }) {
  const inputRef = useRef(null);
  const { pct, upload } = useUploader();

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) { return; }
    if (file.size > 10 * MB) { toast.error("File is too large (max 10 MB)."); return; }
    const form = new FormData();
    form.append("rule_code", ruleCode);
    form.append("file", file);
    try {
      await upload(`/properties/${propertyId}/documents`, form);
      toast.success(`${file.name} attached`);
      onChanged();
    } catch (err) { toast.error(errText(err, "Upload failed")); }
  };

  const remove = async (doc) => {
    if (!window.confirm(`Remove ${doc.original_filename}?`)) { return; }
    try {
      await api.delete(`/files/${doc.id}`);
      toast.success("Document removed");
      onChanged();
    } catch (err) { toast.error(errText(err, "Could not remove")); }
  };

  return (
    <div className="mt-4 pt-4 border-t border-slate-100 flex flex-wrap items-center gap-2" data-testid={`docs-${ruleCode}`}>
      {docs.map((d) => (
        <DocChip key={d.id} doc={d} onOpen={() => onPreview(d)} onRemove={() => remove(d)} />
      ))}
      <button type="button" onClick={() => inputRef.current?.click()} disabled={pct !== null}
        data-testid={`doc-attach-${ruleCode}`}
        className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-dashed border-slate-300 text-slate-600 hover:border-sky-400 hover:text-sky-700 disabled:opacity-60 transition-colors">
        {pct !== null ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading {pct}%</> : <><Paperclip className="w-3.5 h-3.5" /> Attach permit or document</>}
      </button>
      <input ref={inputRef} type="file" accept="application/pdf,image/jpeg,image/png,image/webp" className="hidden"
        onChange={onPick} data-testid={`doc-input-${ruleCode}`} />
    </div>
  );
}

function DocChip({ doc, onOpen, onRemove }) {
  const Icon = doc.content_type?.startsWith("image/") ? ImageIcon : FileText;
  return (
    <span className="inline-flex items-center max-w-full rounded-lg border border-slate-200 bg-slate-50 text-xs text-slate-700" data-testid={`doc-chip-${doc.id}`}>
      <button type="button" onClick={onOpen} data-testid={`doc-open-${doc.id}`}
        className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1.5 min-w-0 hover:text-sky-700 transition-colors">
        <Icon className="w-3.5 h-3.5 flex-shrink-0" />
        <span className="truncate max-w-[180px]">{doc.original_filename}</span>
        <span className="text-slate-400 font-mono">{fmtSize(doc.size)}</span>
      </button>
      <button type="button" onClick={onRemove} aria-label="Remove document" data-testid={`doc-remove-${doc.id}`}
        className="px-1.5 py-1.5 text-slate-400 hover:text-rose-600 transition-colors">
        <X className="w-3.5 h-3.5" />
      </button>
    </span>
  );
}

function useBlobUrl(doc) {
  const [state, setState] = useState({ url: null, error: false });
  useEffect(() => {
    if (!doc) { setState({ url: null, error: false }); return undefined; }
    let alive = true;
    let objectUrl = null;
    setState({ url: null, error: false });
    api.get(`/files/${doc.id}/content`, { responseType: "blob" })
      .then((r) => {
        objectUrl = URL.createObjectURL(r.data);
        if (alive) { setState({ url: objectUrl, error: false }); } else { URL.revokeObjectURL(objectUrl); }
      })
      .catch(() => { if (alive) { setState({ url: null, error: true }); } });
    return () => { alive = false; if (objectUrl) { URL.revokeObjectURL(objectUrl); } };
  }, [doc]);
  return state;
}

export function DocPreviewDialog({ doc, onClose }) {
  const { url, error } = useBlobUrl(doc);
  const isImage = doc?.content_type?.startsWith("image/");
  return (
    <Dialog open={Boolean(doc)} onOpenChange={(open) => { if (!open) { onClose(); } }}>
      <DialogContent className="max-w-3xl" data-testid="doc-preview-dialog">
        <DialogHeader>
          <DialogTitle className="truncate pr-6 font-display">{doc?.original_filename}</DialogTitle>
          <DialogDescription>{doc ? `${fmtSize(doc.size)} · uploaded ${new Date(doc.created_at).toLocaleDateString()}` : ""}</DialogDescription>
        </DialogHeader>
        <div className="h-[60vh] bg-slate-50 rounded-lg border border-slate-200 flex items-center justify-center overflow-hidden">
          {error && <p className="text-sm text-slate-500" data-testid="doc-preview-error">Couldn't load this file. Please try again.</p>}
          {!error && !url && <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />}
          {url && doc && isImage && <img src={url} alt={doc.original_filename} className="max-h-full max-w-full object-contain" data-testid="doc-preview-image" />}
          {url && doc && !isImage && <iframe src={url} title={doc.original_filename} className="w-full h-full" data-testid="doc-preview-pdf" />}
        </div>
        {url && doc && (
          <a href={url} download={doc.original_filename} data-testid="doc-download-btn"
            className="inline-flex items-center justify-center gap-2 self-end rounded-lg bg-slate-900 text-white px-4 py-2 text-sm hover:bg-slate-800 transition-colors">
            <Download className="w-4 h-4" /> Download
          </a>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function PhotoControls({ property, onChanged }) {
  const inputRef = useRef(null);
  const { pct, upload } = useUploader();

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) { return; }
    if (file.size > 8 * MB) { toast.error("Photo is too large (max 8 MB)."); return; }
    const form = new FormData();
    form.append("file", file);
    try {
      await upload(`/properties/${property.id}/photo`, form);
      toast.success("Property photo updated");
      onChanged();
    } catch (err) { toast.error(errText(err, "Upload failed")); }
  };

  const reset = async () => {
    try {
      await api.delete(`/properties/${property.id}/photo`);
      toast.success("Photo removed");
      onChanged();
    } catch (err) { toast.error(errText(err, "Could not remove photo")); }
  };

  const btn = "inline-flex items-center gap-1.5 rounded-lg bg-white/90 backdrop-blur-md text-slate-800 px-3 py-1.5 text-xs font-medium hover:bg-white disabled:opacity-60 transition-colors";
  return (
    <div className="absolute top-4 right-4 z-10 flex gap-2">
      <button type="button" className={btn} onClick={() => inputRef.current?.click()} disabled={pct !== null} data-testid="property-photo-upload-btn">
        {pct !== null ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> {pct}%</> : <><Camera className="w-3.5 h-3.5" /> {property.photo_file_id ? "Change photo" : "Upload photo"}</>}
      </button>
      {property.photo_file_id && (
        <button type="button" className={btn} onClick={reset} data-testid="property-photo-remove-btn">
          <RotateCcw className="w-3.5 h-3.5" /> Use default
        </button>
      )}
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onPick} data-testid="property-photo-input" />
    </div>
  );
}
